import React, { useCallback, useContext, useEffect, useMemo, useReducer, useRef, useState } from "react";
// import { useTable, usePagination, useSortBy, useRowSelect, useFilters, useBlockLayout, useResizeColumns } from "react-table";
import { useReactTable, getCoreRowModel, getPaginationRowModel, getSortedRowModel, flexRender } from "@tanstack/react-table";
import "react-datepicker/dist/react-datepicker.css";
import "react-calendar/dist/Calendar.css";
import { v4 as uuidv4 } from "uuid";
import BasicInput from "components/input/BasicInput";
import Float from "components/input/Float";
import { isEmpty, isSame } from "js/common";
import CommonCodeSelect from "components/input/CommonCodeSelect";
import BasicSelect from "components/input/BasicSelect";
import DayPicker from "components/input/DayPicker";
import BasicCheckbox from "components/input/BasicCheckbox";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlusSquare, faTrashCan } from "@fortawesome/free-solid-svg-icons";
import { sendPostFile } from "api/restUtil";
import { downloadFile } from "api/commonUtil";
import DatePickerInput from "components/input/DatePickerInput";
import DataTablePaging from "./DataTablePaging";

// ReactDataTable. 단순 Display만. 수정은 가능하지만, 각종 처리는 부모쪽에서..
const ReactDataTableModifiable = (props) => {
	const {
		dataTableId,	// 테이블 ID
		columns, //컬럼
		customDatas, //부모에서 주는 데이터 -> inisitalDatas 변수명변경
		returnSelectRows, //부모로 선택한 row 배열 리턴
		hideCheckBox, //체크박스 상태 플래그
		// realTime,	// 실시간 데이터 전달 여부
		isSingleSelect, //단일 체크박스 선택 여부
		isPageNation,	// 페이징 여부
		defaultPageSize, // 기본 페이징 사이즈
		isSpecialRow,	// 페이지 마지막 줄에 배경색
		isLastSpecialRow,	// 데이터 마지막 줄에 배경색
		rowClick,	// row(tr) 클릭 시 이벤트
		specialRow,	// 배경색 다르게 할 row의 key/value. key의 value가 일치하는 row의 배경색 처리
		evenRowClass,	// 짝수열 tr에 적용할 class
		oddRowClass,	// 홀수열 tr에 적용할 class
		rowDraggable,	// 행 Drag가능 여부
		onRowDragDrop,		// 행 Drag&Drop 시 실행 Function
		pagingInfo,		// 데이터를 미리 paging 해서 넘기는 방식에서의 페이징 정보
		// dataSendFlag,	// 부모에게 데이터 전송 여부 (true로 변경될때 데이터 전송)
		dataSend,		// 부모에게 데이터 전송 function
		checkUpdatedFlag,	// 수정 데이터 존재 체크 여부 (true로 변경될때 존재여부 전송)
		checkUpdated,	// 수정 데이터 존재 여부 부모에게 전송 function
		// addRowData, 	// Row 추가시 더해질 데이터

		changeFlagYn,    //변경플래그가 있는지

		tableWidth="100%",	// 전체 테이블 width. percentageWidth가 true일때만 처리. 기본은 columnWidth의 합계.
		percentageWidth = false,	// Column width를 percentage로 설정할지 여부
		maxHeight,
		addRow,	// 행추가 - 추가할 데이터 (Object) (원본 데이터에 영향을 주지 않고 처리 하기 위함)
		delRow,	// 행삭제 - 삭제할 데이터 (Array) (KEY 데이터만 담겨 있어야 함. KEY데이터로 비교)
		delRowComplete,	// 행삭제 완료시 호출할 function
	} = props;

	const [originalTableData, setOriginalTableData] = useState([]);
	const [tableData, setTableData] = useState([]);
	const chkAll = useRef(null);	// 전체선택 체크박스

	useEffect(() => {
		if(customDatas) {
			setTableData(JSON.parse(JSON.stringify(customDatas))); //수정 데이터
			setOriginalTableData(JSON.parse(JSON.stringify(customDatas))); //원본 데이터
		}
	}, [customDatas]);

	useEffect(() => {
		if(addRow) {
			setTableData([...tableData, addRow]);
		}
	}, [addRow]);
	
	useEffect(() => {
		if(delRow && delRow.length > 0) {
			// a가 원본, b가 삭제기준
			const compare = (a, b) => {
				const keys = Object.keys(b);
				for(const key of keys) {
					if(a[key] !== b[key]) {
						return false;
					}
				}
				return true;
			}
			let newTableData = [...tableData];
			for(const delR of delRow) {
				newTableData = newTableData.filter(d => !compare(d, delR));
			}
			const rows = table.getPaginationRowModel().rows;
			for(const delR of delRow) {
				const deletedRow = rows.find(row => compare(row.original, delR));
				if(deletedRow && deletedRow.getIsSelected()) {
					deletedRow.toggleSelected(false);
				}
			}
			setTableData(newTableData);
			delRowComplete && delRowComplete(newTableData);
		}
	}, [delRow]);

	// useEffect(() => {
	// 	realTime && realTime(tableData);
	// }, [tableData]);

	const columnsConfig = useMemo(
		//컬럼 초기 상태
		() =>
			columns.map((column) => ({
				accessorKey: column.col,
				header: column.header,
				// accessor: column.col,
				// sortable: true,
				sortable: false,
				width: column.notView ? 0 : column.cellWidth,
				type: column.type,
				options: column.options,
				notView: column.notView,
				disabled: column.disabled,
				require: column.require,
				textAlign: column.textAlign,
				col: column.col,
				mod: column.mod,	// 수정여부
				rowMod: column.rowMod,	// 데이터별 수정여부 function
				grpCd: column.grpCd,	// 공통코드의 경우 카테고리코드
				valNm: column.valNm,	// 공통코드 Select인 경우 value를 이름으로 설정할지 여부 default=false
				option: column.option,	// Select인 경우 Option
				decimalPlaceCnt: column.decimalPlaceCnt,	// 숫자의 경우, 소수점 자리수
				maxVal: column.maxVal,	// 숫자인 경우 최대값
				minVal: column.minVal,	// 숫자인 경우 최소값
				fileUsageCd: column.fileUsageCd,	// type: file 인 경우 파일사용처코드
				addComma: column.addComma,	// 숫자인 경우 콤마 처리 여부 default true
			})),
		[columns]
	);
/*
	const {
		getTableProps,
		getTableBodyProps,
		headerGroups,
		prepareRow,
		page,
		state: { pageIndex, pageSize },
		previousPage,
		nextPage,
		canPreviousPage,
		canNextPage,
		pageOptions,
		gotoPage,
		setPageSize,
		pageCount,
		selectedFlatRows, // 선택된 행 데이터
		toggleAllRowsSelected, // 전체선택 on off
		toggleRowSelected,
	} = useReactTable(
		{
			columns: columnsConfig,
			data: tableData,
			initialState: { pageIndex: 0, pageSize: isPageNation ? defaultPageSize || 10 : (tableData && tableData.length) || 200 }, // 초기값
			
			getCoreRowModel: getCoreRowModel(), 
			getPaginationRowModel: getPaginationRowModel(),
			getSortedRowModel: getSortedRowModel(),
		},
		// useFilters,
		// useSortBy,
		// usePagination,
		// useRowSelect,
		// useBlockLayout,
		// useResizeColumns,
		(hooks) => {
			hooks.visibleColumns.push((columns) => [
				...(hideCheckBox !== undefined && hideCheckBox
					? []
					: [
						{
							id: "selection",
							// Header: ({ getToggleAllPageRowsSelectedProps }) => (
							Header: ({ getToggleAllPageRowsSelectedProps, rows }) => (
								<>
									{isSingleSelect ? (
										""
									) : (
										<div>
											<input
												id={uuidv4()}
												ref={chkAll}
												type="checkbox"
												{...getToggleAllPageRowsSelectedProps()}
												className="table-checkbox"
												indeterminate="true"
												checked={rows.filter(r => !r.original.isDisabled).length > 0 && rows.filter(r => !r.original.isDisabled).length === rows.filter(r => !r.original.isDisabled && r.isSelected).length}
											/>
										</div>
									)}
									</>
							),
							Cell: ({ row, rows }) => {
								const props = row.original.isDisabled ? null : row.getToggleRowSelectedProps();
								return (
								<div>
									<input
										id={uuidv4()}
										type="checkbox"
										// {...row.getToggleRowSelectedProps()}
										{...props}
										className="table-checkbox"
										indeterminate="false"
										onClick={(e) => checkBoxClick(e, row, rows)}
										disabled={row.original.isDisabled}
										// disabled={row.original.usedYn === "Y" ? true : false}
									/>
								</div>
							)},
							width: 35,
						},
					]),
				...columns,
			]);
		}
	);
	*/

   const table = useReactTable({
	  columns: columnsConfig,
	  data: tableData,
	  getCoreRowModel: getCoreRowModel(),
   });

	/*
	// 테이블 체크박스 클릭 시
	const checkBoxClick = (e, row, rows) => {
		e.stopPropagation();
		const id = row.id;
		if (isSingleSelect) {
			// 단일 선택 시
			toggleAllRowsSelected(false, false);
			toggleRowSelected(id, true); //다시선택
		}
	};
	*/
	
	const getCheckAll = () => {
		const rows = table.getPaginationRowModel().rows;
		return (
			<input
				id={uuidv4()}
				ref={chkAll}
				type="checkbox"
				className="table-checkbox"
				checked={table.getIsAllPageRowsSelected()}
				// indeterminate={table.getIsSomePageRowsSelected()}
				onChange={table.getToggleAllPageRowsSelectedHandler()}
				style={{visibility: isSingleSelect ? "hidden" : "visible"}}
			/>
		)
	};
	const getCheckRow = (row) => {
		const rows = table.getPaginationRowModel().rows;
		return (
			<input
				id={uuidv4()}
				type="checkbox"
				className="table-checkbox"
				// indeterminate="false"
				// onClick={(e) => checkBoxClick(e, row, rows)}
				disabled={row.original.isDisabled}
				checked={row.getIsSelected()}
				onChange={(e) => checkBoxClick(e, row, rows)}
			/>
		)
	}
	
	const checkBoxClick = (e, row, rows) => {
		e && e.stopPropagation();
		// const id = row.id;
		if (isSingleSelect) {
			// 단일 선택 시
			table.toggleAllRowsSelected(false, false);
			row.toggleSelected();
		} else {
			// 단일 선택 아닐때
			row.toggleSelected();

			// 전체 선택 여부에 따라 전체 선택 체크박스 처리
			// if(rows.filter(r => !r.original.isDisabled).length === rows.filter(r => !r.original.isDisabled && (r.id === row.id ? !r.isSelected : r.isSelected)).length) {
			// 	// 비활성화 되지 않은 row 전체 선택 됨.
			// 	chkAll.current.checked = true;
			// } else {
			// 	chkAll.current.checked = false;
			// }
		}
	};

	/*
	// 테이블 체크박스 선택 변경시
	useEffect(() => {
		const selects = selectedFlatRows.filter(row => !row.original.isDisabled).map((row) => row.values);	// disabled 되지 않은 데이터만
		returnSelectRows && returnSelectRows(selects); //부모로 모든 선택 데이터 리턴
	}, [selectedFlatRows]);
	*/
	
	// 테이블 체크박스 선택 변경시
	useEffect(() => {
		const selectedRows = table.getSelectedRowModel().rows;
		const selects = selectedRows && selectedRows.map((row) => row.original);

		returnSelectRows && returnSelectRows(selects); //부모로 모든 선택 데이터 리턴
		// const selects = table.selectedFlatRows && table.selectedFlatRows.map((row) => row.values);
		// returnSelectRows && returnSelectRows(selects); //부모로 모든 선택 데이터 리턴
	}, [table.getSelectedRowModel().rows]);
	// }, [table.selectedFlatRows]);

	// 테이블 row (tr) 클릭 시
	const onTrClick = (row) => {
		// rowClick && rowClick(row.original);
		if(rowClick) {
			if((typeof rowClick).toLowerCase() === "function") {
				rowClick(row.original);
			} else if(rowClick === "checkbox" && !hideCheckBox) {
				// row 클릭 시 체크박스 선택
				table.toggleRowSelected(row.id, !row.isSelected);
				// toggleRowSelected(row.id, !row.isSelected);
				checkBoxClick(null, row, table.getPaginationRowModel().rows);
			}
		}
	}

	const textAlignStyle = (column) => {
		switch (column.textAlign) {
			case "left":
				return "txt-left";
			case "right":
				return "txt-right";
			default:
				return "txt-center";
		}
	};

	let colIdx = null;
	// 드래그
	const handleDragStart = (e, columnIndex) => {
		colIdx = columnIndex;	// 시작 Obj의 index 저장.
	};
	// 드롭
	const handleDrop = (e, columnIndex) => {
		if(colIdx === null) return;
		const tmp = tableData.slice(0, tableData.length);	// 원본 복사 (deep)
		const dragObj = tmp[colIdx];
		tmp.splice(colIdx, 1);	// 이동중인 Obj 꺼내고
		tmp.splice(columnIndex, 0, dragObj);	// 현재 위치에 꺼낸 Obj 넣어주고
		setTableData(tmp);

		onRowDragDrop && onRowDragDrop(tmp);	// 부모로 객체 전달
	}
	//////////////////////////////////////////////////////

	/**
	 * input data 변경시
	 */
	const onInputChange = (e, row) => {
		const { value, name } = e.target;
		const index = row.index;

		const updatedTableData = [...tableData];
		updatedTableData[index][name] = value;
		if(changeFlagYn && updatedTableData[index]["changeFlag"] !== "C") {
			updatedTableData[index]["changeFlag"] = "U";
		}
		
		setTableData(updatedTableData);
	}

	/**
	 * Float등 데이터를 직접 받는 경우
	 */
	// const onValueChange = (name, value, row) => {
	// 	const index = row.index;
	// 	const updatedTableData = [...tableData];
	// 	updatedTableData[index][name] = value;
	// 	if(changeFlagYn && updatedTableData[index]["changeFlag"] !== "C") {
	// 		updatedTableData[index]["changeFlag"] = "U";
	// 	}
	// 	setTableData(updatedTableData);
	// }

	const onSelectChange=(e, row)=> {
		const {value, name} = e.target;
		const index = row.index;

		const updatedTableData = [...tableData];

		// if(name=== "adjustAnnualLeaveClassCdNm") {
		// 	updatedTableData[index]["adjustAnnualLeaveClassCd"] = value;
		// } else {
		// 	updatedTableData[index][name] = value;
		// }
		updatedTableData[index][name] = value;

		if(changeFlagYn && updatedTableData[index]["changeFlag"] !== "C") {
			updatedTableData[index]["changeFlag"] = "U";
		}
		setTableData(updatedTableData);
	}

	const handleDateClick = (date, colName, index) => {

		const update= [...tableData];
		update[index][colName] = date;
		if(changeFlagYn && update[index]["changeFlag"] !== "C") {
			update[index]["changeFlag"] = "U";
		}
        setTableData(update);
    };

	const onCheckboxChange=(e, row)=> {
		const {checked, name} = e.target;
		const index = row.index;

		const updatedTableData = [...tableData];
		if(changeFlagYn && updatedTableData[index]["changeFlag"] !== "C") {
			updatedTableData[index]["changeFlag"] = "U";
		}
		updatedTableData[index][name] = checked === true ? "Y" : "N";
		setTableData(updatedTableData)
	}

	///파일
	const uploadFile= useRef(null);
	const handleUpload=()=> {
		uploadFile.current?.click();
	}

	const handleFileUpload=async(e, col, index, usageCd)=> {
		const files = Array.from(uploadFile.current?.files);
		// const usageCd = "USER";
		const resultData = await sendPostFile("/api/upload-file", files, usageCd);
		if(resultData.respCode === "SUCCESS") {
			const fileSeq = resultData.data[0].fileSeq;
			const updateTableData = [...tableData];
			updateTableData[index]["fileSeq"] = fileSeq;
			updateTableData[index][col]= resultData.data[0].origFileNm;

			if(changeFlagYn && updateTableData[index]["changeFlag"] !== "C") {
				updateTableData[index]["changeFlag"] = "U";
			}
			setTableData(updateTableData);
		}
	}
	
	const delFile=(index)=> {
		const updateTableData=[...tableData];
		updateTableData[index]["fileSeq"] = null;
		updateTableData[index]["origFileNm"]=null;
		if(changeFlagYn && updateTableData[index]["changeFlag"] !== "C") {
			updateTableData[index]["changeFlag"] = "U";
		}

		setTableData(updateTableData);
	}

	// Flag가 true로 변경 시 부모에게 데이터 전달
	useEffect(() => {
		//dataSendFlag && 
		dataSend && dataSend(tableData);
	}, [tableData, dataSend])
	// Flag가 true로 변경 시 부모에게 데이터 수정 여부 전달
	useEffect(() => {
		checkUpdatedFlag && checkUpdated && checkUpdated(!isSame(originalTableData, tableData));
	}, [checkUpdatedFlag])


	return (
		<>
			<div id={dataTableId ?? ""}>
				<div className={isPageNation ? "x-scroll" : "table-scroll"} style={maxHeight ? {maxHeight: maxHeight + "px"} : {}} >
					{/* <table {...getTableProps()} className="table-custom table-styled" style={{ tableLayout: "auto" }}> */}
					<table  className="table-custom table-styled" style={{ tableLayout: "auto" }}>
						<thead>
							{/* {headerGroups.map((headerGroup, headerGroupIndex) => { */}
							{table.getHeaderGroups().map((headerGroup, idx) => {
								
								// const headerGroupProps = {...headerGroup.getHeaderGroupProps()};
								const headerGroupProps = {style: {display: "flex"}};
								if(percentageWidth) {
									headerGroupProps.style.width = tableWidth;
								}

								return (
								// <tr {...headerGroup.getHeaderGroupProps()}>
								<tr {...headerGroupProps} key={"header_tr_" + idx}>
									
									{!hideCheckBox && (<th>{getCheckAll()}</th>)}

									{headerGroup.headers.map((column, columnIndex) => {
										if (column.column.columnDef.notView) {
											// notView가 true인 경우, 헤더 셀을 출력하지 않음
											return null;
										}
										
										// const headerProps = {...column.getHeaderProps()};
										
										const headerProps = {style:{
											width: column.column.columnDef.width + "px",
											display: "inline-block",
											boxSizing: "border-box",
										}};
										if(percentageWidth) {
											if(column.id !== "selection") {	// 퍼센트로 width설정일때 (체크박스는 제외)
												headerProps.style.width = headerProps.style.width?.replace("px", "%");
											} else {
												// 체크 박스는 별도 (35px 맞추고, 가운데 정렬하기 위함.)
												headerProps.style.display = "flex";
												headerProps.style.padding = "0 8.5px";	// 전체너비-border-checkbox너비 / 2 = (35-(1*2)-16)/2 = 17/2 = 8.5
												headerProps.style.alignItems = "center";
												headerProps.style.justifyContent = "center";
											}
										}
										return (
											<th
												key={"header_th_" + columnIndex}
												// {...column.getHeaderProps(column.getSortByToggleProps())}
												// {...column.getHeaderProps()}
												{...headerProps}
												id={`header-${column.id}`}
												className={columnIndex === 0 ? "first-column" : ""}>
												{/* {column.render("Header")} */}
												{
													flexRender(
														column.column.columnDef.header,
														column.getContext()
													)
												}
												{column.column.columnDef.require && (<span className="burgundy">*</span>)}
												{/* <div {...column.getResizerProps()} className={`resizer ${column.isResizing ? "isResizing" : ""}`} /> */}
												{/* <span style={{ color: "red", margin: 0 }}>{column.require === true ? "*" : ""}</span> */}
												{/* <span>{column.isSorted ? (column.isSortedDesc ? " 🔽" : " 🔼") : ""}</span> */}
											</th>
										);
									})}
								</tr>
							)})}
						</thead>
						{tableData.length > 0 ? (
							// <tbody {...getTableBodyProps()}>
							<tbody>
								{/* {page.map((row, rowIndex) => { */}
								{table.getPaginationRowModel().rows.map((row, rowIndex) => {
									// prepareRow(row);
									// const isPageLastRow = isSpecialRow && row.index === page.length - 1;
									const isPageLastRow = isSpecialRow && row.index === table.getPaginationRowModel().rows.length -1;
									const isLastRow = isLastSpecialRow && row.index === tableData.length - 1;
									const customSpecialRow = specialRow && specialRow.val === row.original[specialRow.key];
									
									// const rowsProps = {...row.getRowProps()};
									const rowsProps = {style:{display: "flex"}};
									if(percentageWidth) {
										rowsProps.style.width = tableWidth;
									}

									return (
										// <tr {...row.getRowProps()} className={isPageLastRow || isLastRow || customSpecialRow ? "special-row" : ""} onClick={() => onTrClick(row)} 
										// <tr {...rowsProps} className={isPageLastRow || isLastRow || customSpecialRow ? "special-row" : ""} onClick={() => onTrClick(row)} 
										// <tr {...rowsProps}
										// draggable={rowDraggable} onDragStart={e=> rowDraggable && handleDragStart(e, rowIndex)} onDragOver={e=>rowDraggable && e.preventDefault()} 
										// onDrop={e=>rowDraggable && handleDrop(e, rowIndex)}>
										<tr {...rowsProps}
											key={"body_tr_" + rowIndex}
											className={
												(isPageLastRow || isLastRow ? "special-row" : "")
												+ (customSpecialRow ? (specialRow.class ?? "custom-special-row") : "")
												+ (evenRowClass && rowIndex % 2 === 1 ? " " + evenRowClass : "")
												+ (oddRowClass && rowIndex % 2 === 0 ? " " + oddRowClass : "")
												+ ((typeof rowClick).toLowerCase() === "function" ? " cursor-pointer" : "")
											}
											onClick={() => onTrClick(row)} 
											draggable={rowDraggable}
											onDragStart={e=> rowDraggable && handleDragStart(e, rowIndex)}
											onDragOver={e=>rowDraggable && e.preventDefault()} 
											onDrop={e=>rowDraggable && handleDrop(e, rowIndex)}
											// style={{background: "#ccf1ff30"}}
										>
											
											{!hideCheckBox && (<th>{getCheckRow(row)}</th>)}
											{/* {row.cells.map((cell, cellIndex) => { */}
											{row.getAllCells().map((cell, cellIndex) => {
												if (cell.column.notView) {
													// notView가 true인 경우, 셀을 출력하지 않음
													return null;
												}
												// const cellProps = {...cell.getCellProps()};
												const cellProps = {style:{
													width: cell.column.columnDef.width + "px",
													display: "inline-block",
													boxSizing: "border-box",
												}};
												if(percentageWidth) {
													if(cell.column.id !== "selection") {	// 퍼센트로 width설정일때 (체크박스는 제외)
														cellProps.style.width = cellProps.style.width?.replace("px", "%");
													} else {
														// 체크 박스는 별도 (35px 맞추고, 가운데 정렬하기 위함.)
														cellProps.style.display = "flex";
														cellProps.style.padding = "0 8.5px";	// 전체너비-border-checkbox너비 / 2 = (35-(1*2)-16)/2 = 17/2 = 8.5
														cellProps.style.alignItems = "center";
														cellProps.style.justifyContent = "center";
													}
												}

												const inputItem = {
													mod: cell.column.columnDef.mod,
													rowMod: cell.column.columnDef.rowMod,
													type: cell.column.columnDef.type,
													col : cell.column.columnDef.col, 
													id: cell.column.columnDef.id,
													placeholder: cell.column.columnDef.placeholder,
													maxVal: cell.column.columnDef.maxVal, 
													minVal: cell.column.columnDef.minVal, 
													decimalPlaceCnt: cell.column.columnDef.decimalPlaceCnt, 
													disabled: cell.column.columnDef.disabled, 
													option: cell.column.columnDef.option, 
													checkValue: cell.column.columnDef.checkValue, 
													uncheckValue: cell.column.columnDef.uncheckValue, 
													label: cell.column.columnDef.label,
													addComma: cell.column.columnDef.addComma,
												}

												return (
													<td {...cellProps} className={textAlignStyle(cell.column)} id="otherCol" key={"body_td_" + cellIndex}>
														{
															// cell.column.col !== "leaveTypeCd" && (row.original.modify  || cell.column.mod ||  row.original.usedYn ==="N") ? (
															inputItem.mod 	// 컬럼 자체 설정
															&& (isEmpty(row.original.modify) || row.original.modify)	// 행 설정
															&& (typeof inputItem.rowMod !== "function" || inputItem.rowMod(row.values)) 	// 행의 컬럼 설정
															 ? (
																// 수정 가능
																inputItem.type === "text" ? (
																	<BasicInput
																		item={inputItem}
																		onChange={e => onInputChange(e, row)}
																		value={cell.getValue()}
																		readOnly={false}
																		type="text"
																		textAlign={inputItem.textAlign}
																	/>
																) : inputItem.type === "number" ? (
																	// <BasicInput type="number" item={cell.column} onChange={(e)=> onInputChange(e,row)} value={cell.getValue()}  />
																	<Float
																		className="basic-input"
																		name={inputItem.col}
																		value={cell.getValue() ?? ""}
																		decimalPlaceCnt={0}
																		maxVal={(typeof inputItem.maxVal) !== "function" ? inputItem.maxVal : inputItem.maxVal(row.values)}
																		minVal={(typeof inputItem.minVal) !== "function" ? inputItem.minVal : inputItem.minVal(row.values)}
																		onChange={(e)=> onInputChange(e, row)}
																		addComma={inputItem.addComma}
																	/>
																	// <Float className="basic-input" name={inputItem.id} value={cell.getValue() ?? ""} decimalPlaceCnt={0} onValueChange={(d)=> onValueChange(inputItem.col, d, row)} />
																) : inputItem.type === "decimal" ? (
																	<Float
																		className="basic-input"
																		name={inputItem.col}
																		value={cell.getValue() ?? ""}
																		decimalPlaceCnt={inputItem.decimalPlaceCnt ?? 2}
																		maxVal={(typeof inputItem.maxVal) !== "function" ? inputItem.maxVal : inputItem.maxVal(row.values)}
																		minVal={(typeof inputItem.minVal) !== "function" ? inputItem.minVal : inputItem.minVal(row.values)}
																		onChange={(e)=> onInputChange(e, row)}
																		addComma={inputItem.addComma}
																	/>
																	// <Float className="basic-input" name={inputItem.id} value={cell.getValue() ?? ""} decimalPlaceCnt={inputItem.decimalPlaceCnt ?? 2} onValueChange={(d)=> onValueChange(inputItem.col, d, row)} />
																) : inputItem.type === "select" ? (
																	<BasicSelect item={inputItem} onChange={(e) => onSelectChange(e, row)} value={cell.getValue() ?? ""} />
																// ) : inputItem.type === "select_adjustLeave" ? (
																// 	<BasicSelect item={cell.column} onChange={(e) => onSelectChange(e, row)} value={cell.row.values.adjustAnnualLeaveClassCd ?? ""} />
																) : inputItem.type === "commonCodeSelect" ? (
																	<CommonCodeSelect item={cell.column} onChange={(e) => onInputChange(e, row)} value={cell.getValue() ?? ""} grpCd={inputItem.grpCd} valNm={inputItem.valNm ?? false} />
																// ) : inputItem.type === "commonCodeSelect_Etc" ? (
																// 	<CommonCodeSelect item={cell.column} onChange={(e) => onInputChange(e, row)} value={cell.getValue() ?? ""} grpCd={"HR_ETC_ITEM_CD"} valNm={inputItem.valNm ?? false} />
																) : inputItem.type === "dayPicker" ? ( 
                                                                    <DayPicker
                                                                        name={inputItem.id}
                                                                        autoComplete="off"
                                                                        value={tableData[row.index][inputItem.id] ? tableData[row.index][inputItem.id] : ""}
                                                                        onClick={(data) => handleDateClick(data, inputItem.id, row.index)}
                                                                    />
                                                                ) : inputItem.type === "dayPickerInput" ? ( 
                                                                    <DatePickerInput
                                                                        name={inputItem.id}
                                                                        autoComplete="off"
                                                                        value={tableData[row.index][inputItem.id] ? tableData[row.index][inputItem.id] : ""}
                                                                        onChange={(e) => onInputChange(e, row)}
                                                                    />
                                                                ): inputItem.type === "checkbox" ? ( 
                                                                    <BasicCheckbox item={cell.column} onChange={(e)=> {onCheckboxChange(e,row)}} value={cell.getValue()}   />
                                                                ): inputItem.type === "file" ? (
																	cell.getValue() ?
																	<>
																		<span style={{cursor: "pointer"}} onClick={()=> downloadFile(tableData[row.index]["fileSeq"])}>{tableData[row.index]["origFileNm"]}</span>
																		<FontAwesomeIcon icon={faTrashCan} className="button mg-l-5" size="lg" onClick={()=>delFile(row.index)} />
																		{/* <Button onClick={()=>delFile(row.index)}><FontAwesomeIcon icon={faTrashCan} className="button mg-l-5" size="lg"/></Button> */}
																	</>
																	:
																	<>
																		<input type="file" style={{display:"none"}} onChange={(e)=>handleFileUpload(e, inputItem.id, row.index, inputItem.fileUsageCd)} ref={uploadFile} />
																		<FontAwesomeIcon icon={faPlusSquare} className="button mg-l-5" size="lg" onClick={handleUpload} />
																		{/* <Button onClick={handleUpload}><FontAwesomeIcon icon={faPlusSquare} className="button mg-l-5" size="lg"/></Button> */}
																	</>
                                                                ): (
																	// cell.render("Cell") || ""
																	flexRender(
																		cell.column.columnDef.cell,
																		cell.getContext()
																	)
																)

															) : (
																// 수정불가
																cell.column.Header === "연월" && cell.getValue() ? (
																	cell.getValue().substring(0, 7)
																) : typeof cell.getValue() === "number" ? (
																	cell.getValue() && cell.getValue().toLocaleString()
																) : cell.column.type === "number" ? (
																	cell.getValue() && parseInt(cell.getValue()).toLocaleString()
																): cell.column.type === "checkbox" ? ( 
                                                                    <BasicCheckbox item={cell.column} onChange={(e)=> {onCheckboxChange(e,row)}} value={cell.getValue()} disabled={true} />
                                                                ) : (
																	// cell.render("Cell") || ""
																	flexRender(
																		cell.column.columnDef.cell,
																		cell.getContext()
																	)
																)
															)
														}
													</td>
												);
											})}
										</tr>
									);
								})}
							</tbody>
						) : (
							<tbody>
								<tr>
									<td style={{ fontSize: "15px", height: "80px", display: "flex", justifyContent: "center", alignItems: "center"}} className="back-lightgray">
										조회된 데이터가 없습니다.
									</td>
								</tr>
							</tbody>
						)}
					</table>
				</div>
				{/* {isPageNation && (
					<div className="me-pagenation">
						<button onClick={() => gotoPage(0)} disabled={!canPreviousPage}>
							{" "}
							처음{" "}
						</button>
						<button onClick={() => previousPage()} disabled={!canPreviousPage}>
							{" "}
							이전{" "}
						</button>
						<span>
							{" "}
							페이지 {pageIndex + 1} / {pageOptions && pageOptions.length}{" "}
						</span>
						<button onClick={() => nextPage()} disabled={!canNextPage}>
							{" "}
							다음{" "}
						</button>
						<button onClick={() => gotoPage(pageCount - 1)} disabled={!canNextPage}>
							{" "}
							마지막{" "}
						</button>
					</div>
				)} */}
				{isPageNation && (
					<div className="me-pagenation">
					 <button onClick={() => table.firstPage()} disabled={!table.getCanPreviousPage()}>
						{" "}
						처음{" "}
					 </button>
					 <button onClick={() => table.previousPage()} disabled={!table.getCanPreviousPage()}>
						{" "}
						이전{" "}
					 </button>
					 <span>
						{" "}
						페이지 {table.PaginationState.pageIndex + 1} / {table.getPageCount()}{" "}
					 </span>
					 <button onClick={() => table.nextPage()} disabled={!table.getCanNextPage()}>
						{" "}
						다음{" "}
					 </button>
					 <button onClick={() => table.lastPage()} disabled={!table.getCanNextPage()}>
						{" "}
						마지막{" "}
					 </button>
					</div>
				)}
				{pagingInfo && pagingInfo.totalCnt > 0 && (
					<DataTablePaging cntPerPage={pagingInfo.cntPerPage} pageNo={pagingInfo.pageNo} totalCnt={pagingInfo.totalCnt} searchFnc={pagingInfo.searchFnc} searchCond={pagingInfo.searchCond} />
				)}

			</div>
		</>
	);
};

export default ReactDataTableModifiable;